import { PRODUCT_TIERS, PRODUCT_UOMS } from "@shared/types";
import { Button } from "@shared-ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Input } from "@shared-ui/input";
import { Label } from "@shared-ui/label";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@shared-ui/select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { notify } from "@/components/ui/notify";
import { CategoryPicker } from "@/features/catalog/components/category-picker";
import { uomLabel } from "@/features/catalog/lib/format";
import {
	createAdminProduct,
	listAdminBrands,
} from "@/features/catalog/services/catalog-api.service";

/**
 * Create a product.
 *
 * The missing half of plan 008's M1 -- "an operator with no engineering access
 * can create, complete and publish a product end to end". The editor could
 * complete and publish one; nothing could create one, so M1 could not be run at
 * all and the plan's own acceptance criterion was unreachable.
 *
 * ONLY THE FIELDS THE API DEMANDS. `createProductSchema` requires name, SKU,
 * category, tier and UOM; everything else has a default or is optional. A create
 * form that asked for all twenty fields of a product would be a worse version of
 * the editor, and the operator would be filling in specifications before the
 * category that decides which specifications apply.
 *
 * So this creates a DRAFT and hands over to the editor. Status is not offered:
 * a product with no specifications and no image cannot pass the publish gate, so
 * a "create as active" control could only ever produce a rejection.
 */

const NO_BRAND = "__none__";

export function CatalogProductCreatePage(): JSX.Element {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [name, setName] = useState("");
	const [sku, setSku] = useState("");
	const [tier, setTier] = useState<string>("standard");
	const [uom, setUom] = useState("");
	const [categoryId, setCategoryId] = useState("");
	const [categoryName, setCategoryName] = useState("");
	const [brandId, setBrandId] = useState<string>(NO_BRAND);

	/*
	 * Whether this screen is still on-screen when the POST comes back.
	 *
	 * A React Query mutation's callbacks are not cancelled by unmounting, and
	 * `useNavigate` is app-scoped rather than screen-scoped -- so an operator who
	 * changed their mind and went back was still yanked into the new product's
	 * editor seconds later, from whatever page they had moved on to. The product
	 * is created either way; what stops is the hijacked navigation.
	 */
	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	const brandsQuery = useQuery({
		queryKey: ["admin-catalog-brands"],
		queryFn: listAdminBrands,
		staleTime: 5 * 60 * 1000,
	});

	/*
	 * Which fields are missing, named rather than counted.
	 *
	 * The submit button is disabled until they are all present, and a disabled
	 * button with no explanation is the control an operator stares at wondering
	 * what it wants. The publish gate names every blocker at once for the same
	 * reason; this is the same courtesy one screen earlier.
	 */
	const missing: string[] = [];
	if (name.trim() === "") missing.push("name");
	if (sku.trim() === "") missing.push("SKU");
	if (categoryId === "") missing.push("category");
	if (uom === "") missing.push("unit of measure");

	const create = useMutation({
		mutationFn: async () => {
			return createAdminProduct({
				name: name.trim(),
				sku: sku.trim(),
				tier,
				uom,
				categoryId,
				...(brandId === NO_BRAND ? {} : { brandId }),
			});
		},
		onSuccess: async (product) => {
			await queryClient.invalidateQueries({
				queryKey: ["admin-catalog-products"],
			});
			// The draft exists regardless; only the redirect is abandoned.
			if (!mounted.current) return;
			notify.done("Draft created. Fill in its specifications next.");
			// Straight into the editor: a create screen that returns to the list
			// makes the operator find the row they just made before they can
			// continue, on a list sorted by update time and filtered however they
			// left it.
			navigate(`/catalog/products/${product.id}`);
		},
		// No toast here. `apps/admin/src/lib/api-client.ts` already surfaces
		// `data.message` for every non-401/429 failure -- including the duplicate
		// SKU that is by far the likeliest way this fails -- and a second toast of
		// the same sentence is what the editor had to have removed.
	});

	return (
		<div className="grid max-w-3xl gap-5">
			<div className="grid gap-2">
				<Link
					to="/catalog/products"
					className="text-xs text-muted-foreground hover:underline"
				>
					← All products
				</Link>
				<h2 className="text-2xl font-semibold tracking-tight">New product</h2>
				<p className="text-sm text-muted-foreground">
					Creates a draft. Specifications, images and pricing come next, in the
					editor.
				</p>
			</div>

			<Card className="border-slate-200 shadow-none">
				<CardHeader>
					<CardTitle>Identity</CardTitle>
					<CardDescription>
						The category decides which specifications this product needs, so it
						is worth getting right before moving on.
					</CardDescription>
				</CardHeader>
				<CardContent className="grid gap-4 md:grid-cols-2">
					<div className="grid gap-1.5">
						<Label htmlFor="name">Name</Label>
						<Input
							id="name"
							// The server's own cap. Without it a long name is a round trip
							// that comes back "Validation error" -- the error handler
							// flattens every Zod failure to that one phrase, so the operator
							// is told something is wrong but not which field or why.
							maxLength={300}
							value={name}
							disabled={create.isPending}
							onChange={(event) => setName(event.target.value)}
						/>
					</div>

					<div className="grid gap-1.5">
						<Label htmlFor="sku">SKU</Label>
						<Input
							id="sku"
							maxLength={120}
							value={sku}
							disabled={create.isPending}
							onChange={(event) => setSku(event.target.value)}
						/>
						<p className="text-xs text-muted-foreground">
							Unique across the catalogue. BOQ templates resolve products by it.
						</p>
					</div>

					<div className="grid gap-1.5">
						<Label htmlFor="category">Category</Label>
						<CategoryPicker
							id="category"
							value={categoryId || null}
							valueLabel={categoryName || null}
							clearable={false}
							disabled={create.isPending}
							onChange={(category) => {
								setCategoryId(category?.id ?? "");
								setCategoryName(category?.name ?? "");
							}}
						/>
					</div>

					<div className="grid gap-1.5">
						<Label htmlFor="brand">Brand</Label>
						<Select
							value={brandId}
							disabled={create.isPending}
							onValueChange={setBrandId}
						>
							<SelectTrigger id="brand">
								<SelectValue placeholder="No brand" />
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>
									{/* Optional on the API, so it is optional here. A labour
									    line or an unbranded commodity genuinely has none. */}
									<SelectItem value={NO_BRAND}>No brand</SelectItem>
									{brandsQuery.isError ? (
										<SelectItem value="__unavailable__" disabled>
											Brands unavailable
										</SelectItem>
									) : null}
									{(brandsQuery.data ?? []).map((brand) => (
										<SelectItem key={brand.id} value={brand.id}>
											{brand.name}
										</SelectItem>
									))}
								</SelectGroup>
							</SelectContent>
						</Select>
					</div>

					<div className="grid gap-1.5">
						<Label htmlFor="uom">Unit of measure</Label>
						<Select
							value={uom}
							disabled={create.isPending}
							onValueChange={setUom}
						>
							<SelectTrigger id="uom">
								<SelectValue placeholder="Select a unit" />
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>
									{PRODUCT_UOMS.map((value) => (
										<SelectItem key={value} value={value}>
											{uomLabel(value)}
										</SelectItem>
									))}
								</SelectGroup>
							</SelectContent>
						</Select>
					</div>

					<div className="grid gap-1.5">
						<Label htmlFor="tier">Tier</Label>
						<Select
							value={tier}
							disabled={create.isPending}
							onValueChange={setTier}
						>
							<SelectTrigger id="tier">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>
									{PRODUCT_TIERS.map((value) => (
										<SelectItem key={value} value={value}>
											{value}
										</SelectItem>
									))}
								</SelectGroup>
							</SelectContent>
						</Select>
					</div>
				</CardContent>
			</Card>

			<div className="flex flex-wrap items-center gap-3 border-t pt-4">
				<Button
					disabled={missing.length > 0 || create.isPending}
					onClick={() => create.mutate()}
				>
					{create.isPending ? "Creating…" : "Create draft"}
				</Button>
				{missing.length > 0 ? (
					<span className="text-xs text-muted-foreground">
						Still needed: {missing.join(", ")}.
					</span>
				) : null}
			</div>
		</div>
	);
}
